import React, { useEffect, useState } from "react";
import { TextField } from "@mui/material";
import { TemplateExcelDataInfo } from "services/models";
import { IStepperInput } from "components/atoms/StepperInput";
import StepperInputLabel from "components/atoms/StepperInputLabel";
import { formatNumberWithComma, labeltextBreak } from "@utils/index";
import LabelRequired from "components/atoms/LabelRequired";

type INumberInputField = Omit<IStepperInput, "setting" | "onChange"> & {
  setting: TemplateExcelDataInfo;
  onChange: (text: string) => void;
};

const NumberInputField = ({
  setting,
  value,
  onChange,
  label,
  onBlur,
  ...props
}: INumberInputField) => {
  const [text, setText] = useState("");
  const [focus, setFocus] = useState(false);

  useEffect(() => {
    if (focus) return;
    if (setting.comma) {
      setText(
        formatNumberWithComma((value ?? "").toString(), setting.comma_num),
      );
    } else {
      setText((value ?? "").toString());
    }
  }, [value, setting, focus]);

  const maxValue = React.useMemo(() => {
    if (!setting.maxlen) {
      return 999999999999;
    }
    return 10 ** Number(setting.maxlen) - 1;
  }, [setting]);

  const minValue = React.useMemo(() => {
    if (!setting.maxlen) {
      return -99999999999;
    }
    return -(10 ** (Number(setting.maxlen)-1) - 1);
  }, [setting]);

  const maxLength = React.useMemo(() => {
    if (!setting.maxlen) {
      return 12;
    }
    return Number(setting.maxlen);
  }, [setting]);

  const textFieldWidth = React.useMemo(() => {
    let maxlen = !setting.maxlen ? 12 : Number(setting.maxlen);
    maxlen += setting.comma
      ? Math.ceil(maxlen / Number(setting.comma_num)) - 1
      : 0;
    if (maxlen < 8) {
      return 100;
    } else if (maxlen < 16 + 4) {
      // 最大桁数(数値の最大桁数 + カンマ数の最大数)より小さいとき
      return 100 + (maxlen - 7) * 10;
    } else {
      return 180;
    }
  }, [setting]);

  return setting.not_use_stepper ? (
    <TextField
      label={
        setting.required ? (
          <LabelRequired title={labeltextBreak(setting.name)} />
        ) : (
          labeltextBreak(setting.name)
        )
      }
      value={focus ? value : text}
      inputProps={{
        maxLength:focus? maxValue.toString().length:undefined,
      }}
      onChange={(e) => {
        onChange(e.target.value);
      }}
      {...props}
      onFocus={() => {
        setFocus(true);
        setText(text.replace(",", ""));
      }}
      onBlur={(e) => {
        setFocus(false);
        if (onBlur) onBlur(e);
      }}
    />
  ) : (
    <StepperInputLabel
      label={labeltextBreak(setting.name)}
      value={value}
      required={setting.required}
      onChange={onChange}
      maxValue={maxValue}
      minValue={minValue}
      maxLength={maxLength}
      textFieldWidth={textFieldWidth}
      defaultValue={""}
      commaNum={setting.comma ? setting.comma_num : ""}
      {...props}
      onBlur={(e) => {
        setFocus(false);
        if (onBlur) onBlur(e);
      }}
    />
  );
};

export default NumberInputField;
